import { updateSchema } from '../../common/inputSchemas/document';
import { buildAttributeSchema } from '../lib/buildAttributeSchema';
import { createSaveHandler } from '../lib/createSaveHandler';
import { trpc } from '../lib/trpc';
import { Form, type FormFields } from './Form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Anchor, Breadcrumbs, Text } from '@mantine/core';
import { invariant } from 'es-toolkit';
import { use } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Link, useParams } from 'wouter';
import { z } from 'zod';

export const Edit = () => {
  const { documentId } = useParams();
  invariant(documentId, 'no document id given');

  const documentData = use(
    trpc.documents.show.useQuery(
      { documentId },
      {
        experimental_prefetchInRender: true,
      },
    ).promise,
  );
  invariant(documentData, 'documentData is empty');

  const modelData = use(
    trpc.models.show.useQuery(documentData.modelId, {
      experimental_prefetchInRender: true,
    }).promise,
  );
  invariant(modelData, 'modelData is empty');

  const utils = trpc.useUtils();

  const schema = z
    .object({
      attributes: buildAttributeSchema(modelData.schema.fields),
    })
    .extend(updateSchema.pick({ slug: true, title: true }).shape);
  const form = useForm<FormFields>({
    defaultValues: {
      attributes: Object.fromEntries(
        documentData.attributes.map((attr) => [
          attr.fieldId,
          attr.textValue || '',
        ]),
      ),
      slug: documentData.slug || '',
      title: documentData.title,
    },
    resolver: zodResolver(schema),
  });

  const { mutateAsync: updateDocumentAsync } =
    trpc.documents.update.useMutation();

  const submitHandler = createSaveHandler(
    form,
    (data) => updateDocumentAsync({ data, id: documentId }),
    () => utils.documents.show.invalidate({ documentId }),
  );

  return (
    <>
      <div className="p-5">
        <Breadcrumbs>
          {[
            <Anchor
              component={Link}
              key="content"
              to="/content"
            >
              Content
            </Anchor>,
            <Anchor
              component={Link}
              key="content-type"
              to={`/content/${modelData.id}`}
            >
              {modelData.name}
            </Anchor>,
            <Text key="document">{documentData.title}</Text>,
          ]}
        </Breadcrumbs>
      </div>
      <FormProvider {...form}>
        <Form
          modelData={modelData}
          submitHandler={submitHandler}
          title={`Edit ${modelData.name}`}
        />
      </FormProvider>
    </>
  );
};
